import React, { useState, useEffect } from 'react'
import type { Quiz, Question, QuizAttempt } from '@/types/quiz'
import { useStartQuizAttempt, useSubmitQuizAttempt } from '@/hooks/useQuizzes'
import { QuestionDisplay } from './QuestionDisplay'

interface QuizTakingInterfaceProps {
  quiz: Quiz
  questions: Question[]
  enrollmentId: string
  onComplete: (result: QuizAttempt) => void
  onCancel: () => void
}

export const QuizTakingInterface: React.FC<QuizTakingInterfaceProps> = ({
  quiz,
  questions,
  enrollmentId,
  onComplete,
  onCancel
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number | string>>({})
  const [attemptId, setAttemptId] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState(quiz.timeLimitMinutes * 60)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)

  const startAttempt = useStartQuizAttempt()
  const submitAttempt = useSubmitQuizAttempt()

  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1
  const isFirstQuestion = currentQuestionIndex === 0

  // Start quiz attempt on mount
  useEffect(() => {
    let cancelled = false

    startAttempt.mutate(
      { quizId: quiz.id, enrollmentId },
      {
        onSuccess: (attempt) => {
          if (!cancelled) {
            setAttemptId(attempt.id)
          }
        },
        onError: () => {
          if (!cancelled) {
            alert('Failed to start quiz. Please try again.')
            onCancel()
          }
        }
      }
    )

    return () => {
      cancelled = true
    }
  }, [])

  // Timer countdown
  useEffect(() => {
    if (!attemptId) return

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleAutoSubmit()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [attemptId])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleAnswerChange = (questionId: string, answer: number | string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  const handleAutoSubmit = () => {
    if (!attemptId) return
    submitAttempt.mutate(
      { attemptId, answers },
      {
        onSuccess: onComplete,
        onError: () => {
          alert('Failed to submit quiz')
        }
      }
    )
  }

  const handleSubmit = () => {
    if (!attemptId) return

    // Check if all questions answered
    const unansweredCount = questions.filter(q => !answers[q.id]).length

    if (unansweredCount > 0) {
      if (!confirm(`You have ${unansweredCount} unanswered question(s). Submit anyway?`)) {
        return
      }
    }

    setShowSubmitConfirm(true)
  }

  const confirmSubmit = () => {
    if (!attemptId) return

    submitAttempt.mutate(
      { attemptId, answers },
      {
        onSuccess: onComplete,
        onError: () => {
          alert('Failed to submit quiz. Please try again.')
          setShowSubmitConfirm(false)
        }
      }
    )
  }

  if (startAttempt.isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-500 dark:text-text-muted">Starting quiz...</p>
      </div>
    )
  }

  if (!attemptId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">Failed to initialize quiz</p>
        <button onClick={onCancel} className="mt-4 px-4 py-2 bg-gray-100 dark:bg-white/10 rounded-lg text-gray-900 dark:text-white">
          Go Back
        </button>
      </div>
    )
  }

  const answeredCount = Object.keys(answers).length
  const progressPercentage = (answeredCount / questions.length) * 100

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-surface-dark">
      {/* Header */}
      <div className="px-6 py-4 bg-white dark:bg-surface-card border-b border-gray-200 dark:border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{quiz.title}</h2>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            timeRemaining < 60 ? 'bg-red-500/20 text-red-400' : 'bg-primary/20 text-primary'
          }`}>
            <span className="material-symbols-outlined text-[18px]">schedule</span>
            <span className="text-sm font-bold">{formatTime(timeRemaining)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-text-muted mt-2">
          {answeredCount} of {questions.length} questions answered
        </p>
      </div>

      {/* Question Display */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
        <QuestionDisplay
          question={currentQuestion}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={questions.length}
          selectedAnswer={answers[currentQuestion.id]}
          onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
        />
      </div>

      {/* Navigation Footer */}
      <div className="px-6 py-4 bg-white dark:bg-surface-card border-t border-gray-200 dark:border-white/5">
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={handlePrevious}
            disabled={isFirstQuestion}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-gray-500 dark:text-text-muted hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={submitAttempt.isPending}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-green-500 text-white font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[18px]">check_circle</span>
                <span>Submit Quiz</span>
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={isLastQuestion}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-bold hover:bg-primary/90 transition-colors"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-card rounded-lg p-6 max-w-md w-full border border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-green-400">check_circle</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Submit Quiz?</h3>
            </div>

            <p className="text-gray-500 dark:text-text-muted mb-6">
              Are you sure you want to submit? You cannot change your answers after submission.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                disabled={submitAttempt.isPending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                disabled={submitAttempt.isPending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-green-500 text-white font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {submitAttempt.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuizTakingInterface
